import {
  Badge,
  BloomCalendar,
  Button,
  EmptyState,
  Section,
  Skeleton,
  Text,
  type BloomCalendarItem,
} from '@ui';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { useSession } from '../../identity/session';
import {
  useForageCalendar,
  useForageSuggestions,
  type CalendarEntry,
  type ForageCalendar,
} from '../api';
import { period } from '../format';
import { ForageDialog } from './ForageDialog';

const monthShort = new Intl.DateTimeFormat('de-DE', { month: 'narrow' });
const MONTHS = Array.from({ length: 12 }, (_, i) => monthShort.format(new Date(2026, i, 1)));

/** The shifted dates as the usual period of this year. */
function usual(entry: CalendarEntry, year: number) {
  if (!entry.shift) return {};
  return { usualStart: `${year}-${entry.bloom_start}`, usualEnd: `${year}-${entry.bloom_end}` };
}

/** "ca. 10 Tage früher", "ca. 3 Tage später" or "wie üblich". */
function shiftText(days: number, t: TFunction): string {
  if (days === 0) return t('forage.shift.none');
  return t(days < 0 ? 'forage.shift.earlier' : 'forage.shift.later', { count: Math.abs(days) });
}

/** "Blüht jetzt" as badge, "Blüht demnächst" as grey text, otherwise nothing. */
function Status({ entry }: { entry: CalendarEntry }) {
  const { t } = useTranslation();
  if (entry.status === 'blooming')
    return (
      <Badge size="sm" tone="success">
        {t('forage.status.blooming')}
      </Badge>
    );
  if (entry.status === 'soon') return <span>{t('forage.status.soon')}</span>;
  return null;
}

/** The forage calendar of a location as a year timeline (TRA-003, TRA-004). */
export function ForageCard({ locationId, archived }: { locationId: string; archived: boolean }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const calendar = useForageCalendar(locationId);
  const [editing, setEditing] = useState(false);
  const write = can('locations.write') && !archived;
  // Fetched ahead so the edit dialog can offer them right away (TRA-007).
  const suggestions = useForageSuggestions(locationId, write);
  const entries = calendar.data?.entries ?? [];
  const found = suggestions.data?.state === 'fresh' ? suggestions.data.items : [];

  return (
    <Section
      title={t('forage.card.title')}
      variant="content"
      action={
        write &&
        entries.length > 0 && (
          <Button
            variant="plain"
            size="sm"
            aria-label={t('forage.card.edit')}
            onClick={() => setEditing(true)}
          >
            {t('common.edit')}
          </Button>
        )
      }
      footer={
        entries.length > 0 && (
          <>
            {entries.some((e) => e.shift) && `${t('forage.card.legendShifted')} `}
            {calendar.data &&
              calendar.data.attributions.length > 0 &&
              `${t('forage.card.source', { names: calendar.data.attributions.join(', ') })} `}
            <Link to="/standorte/trachtpflanzen">{t('forage.card.catalog')}</Link>
          </>
        )
      }
    >
      {calendar.error && (
        <Text tone="danger" role="alert">
          {errorMessage(calendar.error)}
        </Text>
      )}
      {!calendar.data ? (
        !calendar.error && (
          <div role="status" aria-label={t('common.loading')}>
            <Skeleton lines={4} />
          </div>
        )
      ) : entries.length === 0 ? (
        <EmptyState
          title={t('forage.card.emptyTitle')}
          level={3}
          description={
            write && found.length > 0
              ? t('forage.suggestions.found', { names: found.map((s) => s.plant.name).join(', ') })
              : undefined
          }
          actions={
            write && (
              <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                {t('forage.card.add')}
              </Button>
            )
          }
        />
      ) : (
        <Calendar calendar={calendar.data} />
      )}
      {editing && calendar.data && (
        <ForageDialog
          locationId={locationId}
          calendar={calendar.data}
          onClose={() => setEditing(false)}
        />
      )}
    </Section>
  );
}

function Calendar({ calendar }: { calendar: ForageCalendar }) {
  const { t } = useTranslation();
  const items: BloomCalendarItem[] = calendar.entries.map((e) => {
    const shift = e.shift ? shiftText(e.shift.days, t) : null;
    const estimated = e.shift?.kind === 'estimated';
    const basis = estimated
      ? t('forage.card.estimated')
      : t('forage.card.stations', { count: e.shift?.stations ?? 0 });
    return {
      key: e.plant.id,
      label: e.plant.name,
      meta: (
        <>
          <Status entry={e} />
          <span>{period(e.start, e.end)}</span>
          {shift && <span>{estimated ? `${shift} (${t('forage.shift.estimated')})` : shift}</span>}
          {e.overridden && <span>{t('forage.card.overridden')}</span>}
        </>
      ),
      start: e.start,
      end: e.end,
      ...usual(e, calendar.year),
      status: e.status,
      tooltip: (
        <>
          <strong>{e.plant.name}</strong>
          <span>
            {t('forage.card.thisYear')}: {period(e.start, e.end)}
          </span>
          {e.shift && (
            <span>
              {t('forage.card.usual')}: {period(e.bloom_start, e.bloom_end)} · {shift} · {basis}
            </span>
          )}
          <span>
            {t('forage.plant.nectar')} {e.plant.nectar}/4 · {t('forage.plant.pollen')}{' '}
            {e.plant.pollen}/4
          </span>
          {e.note && <span>{e.note}</span>}
        </>
      ),
    };
  });
  return (
    <BloomCalendar
      label={t('forage.card.title')}
      year={calendar.year}
      today={calendar.today}
      months={MONTHS}
      todayLabel={t('forage.card.today')}
      items={items}
    />
  );
}
